import { useRef, useState } from "react";
import { ActionIcon, Alert, Checkbox, Loader, Paper, ScrollArea, Text, Button } from "@mantine/core";
import { useHotkeys, useViewportSize } from "@mantine/hooks";
import { IconCheck, IconEye, IconEyeOff, IconX } from "@tabler/icons-react";
import { Document, Page } from "react-pdf";
import { optimize } from "svgo";
import classNames from "classnames";
import SignaturePad from "../boarding/SignaturePad";
import { useUser } from "../../providers/UserProvider";
import { getMode } from "../../helpers/helpers";

const ALL_MONTHS = ["červenec", "srpen"];

export default function TaxDeclarationSign({ onClose, onSign, loading }) {
  const signatureRef = useRef();
  const [pathsCount, setPathsCount] = useState(0);
  const [selectedMonths, setSelectedMonths] = useState([]);
  const [numPages, setNumPages] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(true);
  const [show, setShow] = useState(true);
  const { width } = useViewportSize();
  const { user } = useUser();
  const { mode } = getMode();

  useHotkeys([["Escape", onClose]]);

  const handleSign = () => {
    const svgElement = document.createElement("svg");
    const trimElement = signatureRef.current;
    const bBox = trimElement.getBBox();

    svgElement.setAttribute("xmlns", "http://www.w3.org/2000/svg");
    svgElement.setAttribute("viewBox", `${bBox.x} ${bBox.y} ${bBox.width} ${bBox.height}`);
    svgElement.setAttribute("width", bBox.width);
    svgElement.setAttribute("height", bBox.height);
    svgElement.appendChild(trimElement.cloneNode(true));

    const result = optimize(svgElement.outerHTML, { multipass: true, floatPrecision: 2 });

    const parser = new DOMParser();
    const doc = parser.parseFromString(result.data, "image/svg+xml");
    const optimizedSvg = doc.querySelector("svg");
    optimizedSvg.setAttribute(
      "viewBox",
      `${Math.round(bBox.x)} ${Math.round(bBox.y)} ${Math.round(bBox.width)} ${Math.round(bBox.height)}`
    );
    optimizedSvg.setAttribute("width", Math.round(bBox.width));
    optimizedSvg.setAttribute("height", Math.round(bBox.height));

    const serialized = new XMLSerializer().serializeToString(optimizedSvg);
    onSign(serialized, selectedMonths);
  };

  const canSign = pathsCount > 0 && selectedMonths.length > 0;

  return (
    <div
      className={classNames("absolute left-0 top-0 z-20 flex h-full w-full flex-col justify-center", {
        "bg-dark-200": mode === "dark",
        "bg-dark-900": mode === "light",
      })}>
      <ScrollArea className="h-full w-full">
        <Document
          file={`${new URL("user/tax-declaration/preview", import.meta.env.VITE_API_URL)}?_token=${user.token}`}
          onLoadSuccess={({ numPages }) => setNumPages(numPages)}
          className="h-full w-full"
          loading={
            <div className="absolute left-0 top-0 flex h-full w-full flex-col items-center justify-center">
              <Loader />
              <Text mt="md">Načítání dokumentu...</Text>
            </div>
          }
          error={
            <div className="absolute left-0 top-0 flex h-full w-full flex-col items-center justify-center text-center">
              <Text className="text-6xl">😰</Text>
              <Text mt="md">Dokument nelze načíst, nejspíše chyba na straně serveru.</Text>
            </div>
          }>
          {Array.from(new Array(numPages), (_, index) => (
            <Page
              renderAnnotationLayer={false}
              renderTextLayer={false}
              key={`page_${index + 1}`}
              className="mx-auto my-4 w-fit"
              loading={null}
              width={Math.min(width, 1000)}
              pageNumber={index + 1}
              onRenderSuccess={() => setPdfLoading(false)}
            />
          ))}
          <div style={{ width: Math.min(width, 1000) }} className="mx-auto h-20" onClick={onClose} />
        </Document>
        {!pdfLoading && (
          <Button
            className="absolute bottom-6 right-6 shadow-md"
            leftSection={show ? <IconEye size="1rem" stroke={1.5} /> : <IconEyeOff size="1rem" stroke={1.5} />}
            onClick={() => setShow(!show)}>
            {show ? "Zobrazit dokument" : "Schovat dokument"}
          </Button>
        )}
      </ScrollArea>
      {show && !pdfLoading && (
        <Paper withBorder className="flex max-h-[90%] flex-col rounded-none border-0 border-t-2 shadow-xl">
          <ScrollArea p="lg">
            <div className="mx-auto w-full max-w-[1000px]">
              <div className="mb-6">
                <Text fw={600} size="sm" mb="xs">
                  Měsíce
                </Text>
                <Checkbox.Group value={selectedMonths} onChange={setSelectedMonths}>
                  <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
                    {ALL_MONTHS.map((month) => (
                      <Checkbox key={month} value={month} label={month} />
                    ))}
                  </div>
                </Checkbox.Group>
              </div>
              <Alert mb="md">Prosím podepište se do pole níže.</Alert>
              <div className="flex h-[350px] gap-4">
                <SignaturePad setPathsCount={setPathsCount} signatureRef={signatureRef} />
                <div className="flex flex-col gap-4">
                  <ActionIcon variant="filled" color="red" size="xl" onClick={onClose}>
                    <IconX size={24} stroke={1.5} />
                  </ActionIcon>
                  <ActionIcon
                    variant="filled"
                    color="teal"
                    size="xl"
                    className="grow"
                    loading={loading}
                    disabled={!canSign}
                    onClick={handleSign}>
                    <IconCheck size={24} stroke={1.5} />
                  </ActionIcon>
                </div>
              </div>
            </div>
          </ScrollArea>
        </Paper>
      )}
    </div>
  );
}
